/**
 * The WIP ladder. MASTER_PROMPT section 2.2.
 *
 * Five states per SKU: on loom, roll, cut, ready bale, and what has been
 * dispatched. The total situation is the four that are still in the plant.
 *
 * Nothing here is a stored balance. Every figure is folded from the ledger at
 * the moment it is asked for, which is what makes Phase 0 finding F7
 * structurally impossible — one SKU held 17,240 pieces in the legacy report
 * while stating a total of nil, because both numbers were typed.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AsOnPicker } from "../../components/AsOnPicker";
import { ReportDownload } from "../../components/ReportDownload";
import { ApiError } from "../../lib/api";
import { ops } from "../../lib/api2";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { DrillCard } from "../../components/Drill";
import type { DrillSpec } from "../../lib/controls";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import type { WipLadderLine, WipLadderResponse } from "../../lib/types";
import { fmtDate, fmtQty, today, toNumber } from "../../lib/format";

const COLUMNS: SheetColumn<WipLadderLine>[] = [
  { key: "sku", label: "SKU", value: (r) => r.sku_name },
  { key: "on_loom", label: "On loom", type: "number", numeric: true,
    value: (r) => toNumber(r.on_loom_pcs),
    format: (r) => fmtQty(r.on_loom_pcs, 0) },
  { key: "roll_mtr", label: "Roll (m)", type: "number", numeric: true,
    value: (r) => toNumber(r.roll_mtr), format: (r) => fmtQty(r.roll_mtr, 0) },
  { key: "roll_pcs", label: "Roll (pcs)", type: "number", numeric: true,
    value: (r) => toNumber(r.roll_pcs), format: (r) => fmtQty(r.roll_pcs, 0) },
  { key: "cut", label: "Cut", type: "number", numeric: true,
    value: (r) => toNumber(r.cut_pcs), format: (r) => fmtQty(r.cut_pcs, 0) },
  { key: "ready", label: "Ready bale", type: "number", numeric: true,
    value: (r) => toNumber(r.ready_bale_pcs),
    format: (r) => fmtQty(r.ready_bale_pcs, 0) },
  { key: "situation", label: "Total situation", type: "number", numeric: true,
    value: (r) => toNumber(r.total_situation),
    format: (r) => fmtQty(r.total_situation, 0) },
  { key: "weight", label: "Weight", type: "number", numeric: true,
    value: (r) => toNumber(r.total_kg),
    format: (r) => `${fmtQty(r.total_kg, 0)} kg` },
  { key: "dispatched", label: "Dispatched", type: "number", numeric: true,
    value: (r) => toNumber(r.dispatched_pcs),
    format: (r) => fmtQty(r.dispatched_pcs, 0) },
];

// Which ledger location and materials each rung is folded from — the same
// mapping as `services/wip.py`, so a rung's breakdown is its own movements.
const RUNGS = [
  { key: "on_loom_pcs", label: "On loom", hint: "being woven",
    location: "LOOM_FLOOR", items: ["FABRIC_MTR", "FABRIC_KG"] },
  { key: "roll_pcs", label: "Roll", hint: "woven, not yet cut",
    location: "WIP_ROLL", items: ["FABRIC_MTR", "FABRIC_KG"] },
  { key: "cut_pcs", label: "Cut", hint: "cut, not yet stitched",
    location: "WIP_CUT", items: ["CUT_PCS"] },
  { key: "ready_bale_pcs", label: "Ready bale", hint: "finished, in store",
    location: "FG_STORE", items: ["STITCHED_BAG"] },
] as const;

const rungDrill = (label: string, asOn: string, locations: string[],
                   items: readonly string[]): DrillSpec => ({
  source: "movements",
  group: ["sku", "item", "date"],
  crumbs: [`${label} at ${asOn}`],
  filters: { to: asOn, location_codes: locations, item_codes: [...items] },
});

export function WipLadder() {
  const [asOn, setAsOn] = useState(today);
  const [showEmpty, setShowEmpty] = useState(false);
  const [data, setData] = useState<WipLadderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    ops
      .wipLadder(asOn, showEmpty)
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  }, [asOn, showEmpty]);

  useEffect(load, [load]);

  const sheet = useSheet(useMemo(() => data?.lines ?? [], [data]), COLUMNS);

  async function snapshot() {
    setBusy(true);
    try {
      const r = await ops.wipSnapshot(asOn);
      setNote(
        `Snapshot stored for ${fmtDate(asOn)} — ${r.skus_written} SKU(s). It is a record, not the truth; the ledger stays authoritative.`
      );
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function reconcile() {
    setBusy(true);
    try {
      const r = await ops.wipReconcile(asOn);
      setNote(r.note);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  // Column totals across the ladder, so the rung headers carry a figure.
  const totals = data
    ? RUNGS.map((rung) =>
        data.lines.reduce((sum, l) => sum + Number(l[rung.key] ?? 0), 0)
      )
    : [];

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">WIP ladder</h2>
          <p className="page__sub">
            Five states per SKU, folded from the ledger — never a stored balance
          </p>
        </div>
        <div className="page__controls">
          <AsOnPicker value={asOn} onChange={setAsOn} />
          <ReportDownload title="WIP ladder" period={{ preset: "custom", from: asOn, to: asOn }} />
          <label className="checkbox" style={{ alignSelf: "flex-end" }}>
            <input
              type="checkbox"
              checked={showEmpty}
              onChange={(e) => setShowEmpty(e.target.checked)}
            />
            Show empty SKUs
          </label>
          <button
            className="btn btn--ghost btn--auto"
            onClick={snapshot}
            disabled={busy}
          >
            Snapshot
          </button>
          <button
            className="btn btn--ghost btn--auto"
            onClick={reconcile}
            disabled={busy}
          >
            Reconcile
          </button>
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}
      {note && <div className="banner banner--positive">{note}</div>}

      {data?.warnings.map((w) => (
        <div key={w} className="banner banner--warning">
          {w}
        </div>
      ))}

      {!data && !error && (
        <div className="state">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton state__bar" />
          ))}
        </div>
      )}

      {data && data.lines.length === 0 && (
        <div className="state state--empty">
          <span className="state__glyph" aria-hidden="true">
            ☰
          </span>
          <h3>Nothing in progress</h3>
          <p>
            No work in progress at {fmtDate(data.as_on)}. Fabric appears here as
            soon as a loom shift is recorded against an SKU.
          </p>
        </div>
      )}

      {data && data.lines.length > 0 && (
        <>
          <div className="kpirow">
            {RUNGS.map((rung, i) => (
              <DrillCard key={rung.key}
                         drill={rungDrill(rung.label, data.as_on, [rung.location], rung.items)}>
                <span className="label">{rung.label}</span>
                <span className="kpi-value num">{fmtQty(totals[i])}</span>
                <span className="kpi__sub">{rung.hint}</span>
              </DrillCard>
            ))}
            <DrillCard drill={{
              ...rungDrill("Total situation", data.as_on,
                           RUNGS.map((r) => r.location),
                           [...new Set(RUNGS.flatMap((r) => r.items))]),
              group: ["to", "sku", "item"],
            }}>
              <span className="label">Total situation</span>
              <span className="kpi-value num">
                {fmtQty(data.total_situation)}
              </span>
              <span className="kpi__sub">pieces in the plant</span>
            </DrillCard>
          </div>

          <SheetToolbar sheet={sheet} exportName="wip-ladder" />

          <div className="tablewrap tablewrap--tall">
            <table className="table">
              <SheetHead sheet={sheet} />
              <tbody>
                {sheet.rows.map((line) => {
                  const negative =
                    Number(line.cut_pcs) < 0 ||
                    Number(line.ready_bale_pcs) < 0 ||
                    Number(line.roll_mtr) < 0;
                  return (
                    <tr
                      key={line.sku_code}
                      className={
                        line.unallocated || negative ? "row--warn" : undefined
                      }
                    >
                      <td>
                        {line.unallocated ? (
                          <>
                            <strong>{line.sku_name}</strong>
                            <span className="cell__unit">no SKU recorded</span>
                          </>
                        ) : (
                          line.sku_name
                        )}
                      </td>
                      <td className="num mono">
                        {fmtQty(line.on_loom_pcs, 0)}
                      </td>
                      <td className="num mono">{fmtQty(line.roll_mtr, 0)}</td>
                      <td className="num mono">{fmtQty(line.roll_pcs, 0)}</td>
                      <td
                        className={`num mono${Number(line.cut_pcs) < 0 ? " cell--negative" : ""}`}
                      >
                        {Number(line.cut_pcs) < 0 ? "▼ " : ""}
                        {fmtQty(line.cut_pcs, 0)}
                      </td>
                      <td className="num mono">
                        {fmtQty(line.ready_bale_pcs, 0)}
                      </td>
                      <td className="num mono">
                        <strong>{fmtQty(line.total_situation, 0)}</strong>
                      </td>
                      <td className="num mono">{fmtQty(line.total_kg, 0)} kg</td>
                      <td className="num mono">
                        {fmtQty(line.dispatched_pcs, 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num mono">{fmtQty(totals[0])}</td>
                  <td />
                  <td className="num mono">{fmtQty(totals[1])}</td>
                  <td className="num mono">{fmtQty(totals[2])}</td>
                  <td className="num mono">{fmtQty(totals[3])}</td>
                  <td className="num mono">
                    <strong>{fmtQty(data.total_situation)}</strong>
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="page__note">
            Pieces are derived from metres at each SKU&rsquo;s cut length. The
            legacy Opening-2 report used slightly different per-SKU factors, so
            a roll figure here can differ from that sheet by a few tenths of a
            percent — the conversion is stated rather than tuned to match.
          </p>
        </>
      )}
    </div>
  );
}
